import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspaceState } from '@/contexts/WorkspaceContext'
import { useUserProfile } from '@/hooks/useSupabaseAuth'
import { resolveRoute } from '@/lib/workspace'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileText, Loader2 } from 'lucide-react'

export function LoginForm() {
  const { signIn, user } = useAuth()
  const workspaceState = useWorkspaceState()
  const { data: profile } = useUserProfile()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const activeAccount = workspaceState.kind === 'active' ? workspaceState.account : null
  const branding = activeAccount?.settings?.branding
  const showWorkspaceLogo =
    !!activeAccount && branding?.show_branding_on_public_login === true && !!branding?.logo_url

  // Redirect if already logged in. Shell-aware: admin shell has no `/dashboard` route,
  // so super_admin → `/admin`, non-super_admin without slug → `/no-account` (the
  // cross-subdomain redirect to a workspace happens in AuthContext when a slug exists).
  useEffect(() => {
    if (!user) return

    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname
    if (from && from !== '/login' && from !== '/dashboard') {
      navigate(from, { replace: true })
      return
    }

    const target = resolveRoute()

    if (target.kind === 'workspace') {
      navigate('/dashboard', { replace: true })
      return
    }

    if (target.kind === 'admin') {
      if (!profile) return
      if (profile.role === 'super_admin') {
        navigate('/admin', { replace: true })
        return
      }
      navigate('/no-account', { replace: true })
      return
    }
  }, [user, profile, navigate, location])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await signIn(email, password)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in'
      setError(errorMessage)
    }

    setLoading(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {showWorkspaceLogo ? (
              <img
                src={branding!.logo_url!}
                alt={activeAccount!.name}
                className="h-12 w-auto max-w-[200px]"
              />
            ) : (
              <FileText className="h-12 w-12 text-primary" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {activeAccount ? `Sign in to ${activeAccount.name}` : 'Welcome to iCrop'}
          </CardTitle>
          <CardDescription>
            Sign in to access your document management system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}